import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Slideout — universal right-edge detail panel.
 *
 * Per Design Principles §5.5 + §3.13, the slideout is the canonical
 * "triage" surface. Every clickable inventory row opens a slideout
 * before navigating to the full detail page. Universal tab structure
 * (Summary / Audit / Five-Layer Verdict / [object-specific]).
 *
 * Closes on:
 *   - Click of the X button
 *   - Press of Escape
 *   - Backdrop click (unless dismissOnBackdrop=false)
 *
 * Width default 480 px, override via `width` prop. The panel always
 * spans full viewport height; body scrolls.
 *
 * @param {Object} props
 * @param {boolean}            props.isOpen
 * @param {() => void}         props.onClose
 * @param {React.ReactNode}    [props.header]
 *   Sticky header content. Typically the object name, trust chip, primary actions.
 * @param {Array<{id: string, label: React.ReactNode}>} [props.tabs]
 * @param {string}             [props.activeTabId]
 * @param {(id: string) => void} [props.onTabChange]
 * @param {React.ReactNode}    props.children
 * @param {number}             [props.width=480]
 * @param {boolean}            [props.dismissOnBackdrop=true]
 * @param {string}             [props.ariaLabel]
 */
export function Slideout({
  isOpen,
  onClose,
  header,
  tabs,
  activeTabId,
  onTabChange,
  children,
  /* G378 — Optional sticky action bar. Used by Create-/Edit- slideouts
   * (Group / Connector Group / Escalation Policy) that replaced modals
   * in V2. Stays pinned to the bottom of the panel so primary CTAs
   * remain reachable as the body scrolls. */
  footer,
  width = 480,
  dismissOnBackdrop = true,
  ariaLabel = 'Detail panel',
}) {
  const panelRef = useRef(null);

  // DEF-EXEC-009 — onClose indirection. Every caller passes an inline
  // arrow function (`onClose={() => setX(null)}`) which means the
  // identity changes on every parent re-render. If `onClose` is in
  // this effect's dep array, the keydown listener is torn down and
  // re-bound on every parent render. Under rapid open/close +
  // in-panel typing this creates a race window where a listener
  // bound during commit can outlive the slideout's unmount and
  // continue firing against a stale handler — which manifests as the
  // "clicks don't reach React" symptom in the defect log. Modal.jsx
  // fixed the same anti-pattern via this ref indirection; this is
  // the symmetric fix for Slideout. Effect now only re-runs when
  // isOpen flips, listener identity is stable.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') {
      const cb = onCloseRef.current;
      if (cb) cb();
    } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Always render the DOM so the slide-in animation works on first open.
  // Visibility is driven by the `isOpen` class flip.
  return (
    <>
      <div
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
        className={
          'fixed inset-0 z-40 transition-opacity duration-v2-lg ease-v2 ' +
          (isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')
        }
        style={{ background: 'rgba(15, 23, 42, .35)' }}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-hidden={!isOpen}
        className={
          'fixed top-0 right-0 bottom-0 z-50 ' +
          'bg-v2-surface ' +
          'flex flex-col ' +
          'transition-transform duration-v2-lg ease-v2 ' +
          // RC-131 + RC-132 — Two distinct edge-bleed sources fixed
          // here:
          //
          //   1. box-shadow (RC-131). A closed slideout is parked off-
          //      screen via translate-x-full, but box-shadow paints
          //      OUTSIDE the element box, so shadow-v2-panel bleeds
          //      back into the viewport's right edge.
          //
          //   2. border-l (RC-132). The 1px left border lands at the
          //      effective left edge of the slideout's box AFTER
          //      translate. Even though that x-coordinate is
          //      visually-just-outside the viewport, sub-pixel anti-
          //      aliasing on Retina + DPR>1 displays leaks the border
          //      back into the rightmost visible pixel column. With
          //      ~21 always-mounted slideouts on the Build tab
          //      (Create/Edit/AddExisting/Invoke/GenerateEval/Import
          //      across every skill row), those 21 leaked borders
          //      stack into a visible vertical band line — the
          //      operator-reported "ghost column lines."
          //
          // Both shadow + border move into the isOpen branch so the
          // off-screen aside is paint-free at the rightmost edge.
          // The bg-v2-surface stays unconditional: it's a fill, not
          // an outline, and never paints outside the element box.
          (isOpen
              ? 'border-l border-v2-border shadow-v2-panel translate-x-0'
              : 'translate-x-full')
        }
        style={{ width, maxWidth: '100vw' }}
      >
        <div className="flex-shrink-0 border-b border-v2-border-subtle">
          <div className="px-5 pt-4 pb-3">
            {header}
          </div>
          {tabs && tabs.length > 0 && (
            <div role="tablist" aria-orientation="horizontal" className="flex items-center gap-0 px-3">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => onTabChange && onTabChange(tab.id)}
                    className={
                      'v2-focus-ring px-3 py-2.5 text-[12.5px] font-medium ' +
                      'border-b-2 -mb-px transition-colors duration-v2 ' +
                      (isActive
                        ? 'border-v2-action text-v2-action font-semibold'
                        : 'border-transparent text-v2-ink-muted hover:text-v2-ink')
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className={
              'v2-focus-ring absolute top-3 right-3 w-7 h-7 rounded-v2-sm ' +
              'flex items-center justify-center text-v2-ink-muted ' +
              'hover:bg-v2-muted hover:text-v2-ink ' +
              'transition-colors duration-v2 ease-v2'
            }
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          /* G378 — Sticky action bar (Cancel / Save) for create/edit slideouts. */
          <div className="flex-shrink-0 border-t border-v2-border-subtle bg-v2-surface px-5 py-3">
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}

export default Slideout;
